import { useRef, useState } from 'react';

import { ExtractedTableJson } from '@frontend/api/src/parts/useGetExtractedTables';
import {
  OutlineCircleQuestionIcon,
  Popover,
  PopoverTrigger,
  PopoverContent,
  VStack,
  Portal,
  Divider,
  Button,
  Input,
  ShareExternalIcon,
} from '@frontend/legacy-components';

import TitleBlock from 'components/DesignPrefabs/TitleBlock/TitleBlock';
import TitleBlockDescription from 'components/DesignPrefabs/TitleBlock/TitleBlockDescription';
import TitleBlockTitle from 'components/DesignPrefabs/TitleBlock/TitleBlockTitle';

import { Explanation } from 'modules/common/components';

import { NoBomReasons } from './constants';

type NoBomInfoPopoverProps = {
  showBom: boolean;
  rawTableData: ExtractedTableJson | null;
};

export const NoBomInfoPopover = ({
  showBom,
  rawTableData,
}: NoBomInfoPopoverProps) => {
  const initialRef = useRef<HTMLInputElement>(null);

  const popoverRef = useRef<HTMLInputElement>(null);
  const buttonRef = useRef<HTMLInputElement>(null);
  const [maxHeight, setMaxHeight] = useState('60vh');

  // Calculate max height when the popover opens
  const calculateMaxHeight = () => {
    if (buttonRef.current) {
      const buttonRect = buttonRef.current.getBoundingClientRect();
      const viewportHeight = window.innerHeight;
      const availableSpace = viewportHeight - buttonRect.bottom - 10;
      setMaxHeight(`${availableSpace}px`);
    }
  };

  return (
    !showBom && (
      <Popover
        returnFocusOnClose={false}
        onOpen={calculateMaxHeight}
        gutter={10}
      >
        {/* Hidden input to hold initial focus */}
        <Input
          ref={initialRef}
          position="absolute"
          opacity={0}
          h={0}
          w={0}
          aria-hidden="true"
        />
        <PopoverTrigger>
          <Button
            variant="link"
            color="interaction.main"
            size="md"
            tabIndex={0}
            rightIcon={<OutlineCircleQuestionIcon size={'1.125rem'} />}
          >
            {"Why wasn't this interpreted as a BOM?"}
          </Button>
        </PopoverTrigger>
        {/* Portal the popover content to ensure it is rendered outside the modal 
            This prevents the popover from being clipped if it is taller than its parent modal */}
        <Portal overrideOverlayContainer={true}>
          <PopoverContent
            className="no-bom-popover"
            maxH={maxHeight}
            ref={popoverRef}
            w={43}
          >
            <TitleBlock size="md" px={1.5} pb={1.5} pt={1}>
              <TitleBlockTitle
                size={'lg'}
                mb={1}
                title="Why wasn't this interpreted as a BOM?"
              />
              <TitleBlockDescription
                size={'md'}
                description={
                  <>
                    {
                      'To maintain high-quality suggestions, tables must meet certain criteria before being interpreted as a BOM. '
                    }
                    <Button
                      as="a"
                      variant="link"
                      size="md"
                      href="https://help.paperlessparts.com/s/article/bom-extractions-and-suggestions"
                      target="_blank"
                      icon={<ShareExternalIcon size={2.25} />}
                    >
                      Learn more
                    </Button>
                  </>
                }
              />
            </TitleBlock>
            <Divider />
            <VStack
              spacing={0}
              align="stretch"
              gap={0}
              py={1}
              px={1.5}
              overflow="auto"
            >
              {!rawTableData?.hasBom && (
                <Explanation
                  variant="warning"
                  title={NoBomReasons.MISSING_KEY_COLUMNS.title}
                  description={NoBomReasons.MISSING_KEY_COLUMNS.description}
                />
              )}
              {(rawTableData?.hasMissingQuantity ||
                rawTableData?.hasInvalidQuantity) && (
                <Explanation
                  variant="warning"
                  title={NoBomReasons.MISSING_OR_INVALID_QUANTITIES.title}
                  description={
                    NoBomReasons.MISSING_OR_INVALID_QUANTITIES.description
                  }
                />
              )}
              {rawTableData?.hasInconsistentPartNumberIdentifier && (
                <Explanation
                  variant="warning"
                  title={NoBomReasons.INCONSISTENT_PART_NUMBERS.title}
                  description={
                    NoBomReasons.INCONSISTENT_PART_NUMBERS.description
                  }
                />
              )}
              {rawTableData?.hasInconsistentDescriptionIdentifier && (
                <Explanation
                  variant="warning"
                  title={NoBomReasons.INCONSISTENT_PART_DESCRIPTIONS.title}
                  description={
                    NoBomReasons.INCONSISTENT_PART_DESCRIPTIONS.description
                  }
                />
              )}
            </VStack>
          </PopoverContent>
        </Portal>
      </Popover>
    )
  );
};
